/**
 * Performance / vision runtime config — Prompt.md §31.
 *
 * "Không cần inference 30 FPS." Ưu tiên: nhiệt độ thiết bị, pin, responsiveness.
 */

export interface VisionConfig {
  /** §31 — vision inference 8–15 FPS. */
  targetInferenceFps: number;
  /** Sàn khi tự hạ FPS trên máy yếu. */
  minInferenceFps: number;
  /** §31 — UI update 2–5 FPS. Throttle ở tầng controller, không ở React. */
  uiUpdateFps: number;

  /**
   * p95 của `inferenceMs` vượt mức này → tự hạ `targetInferenceFps` (adaptive degradation).
   * Dải thiết bị của trẻ rất rộng (iPhone SE cũ → flagship Android); tự hạ FPS giữ được
   * responsiveness mà không cần whitelist thiết bị.
   */
  degradeAboveP95Ms: number;

  /** Số mẫu giữ trong ring buffer đo hiệu năng (~5s ở 12 FPS). */
  perfSampleWindow: number;

  /**
   * Quá thời gian này mà provider chưa initialize xong → coi như thất bại.
   *
   * Không có mốc này thì một `initialize()` treo (tải WASM/model bị chặn, WebGL context không
   * cấp được) sẽ hiện "Đang tải bộ nhận diện tư thế…" vĩnh viễn — trên điện thoại thì không có
   * console để biết chuyện gì đang xảy ra.
   *
   * Phải phủ được TRƯỜNG HỢP XẤU NHẤT, không phải trường hợp thường: GPU treo hết
   * `gpuInitTimeoutMs` rồi mới fallback CPU, và fallback là một lần instantiate WASM thứ hai
   * (~11.7 MB SIMD build) cộng nạp lại model ~5.8 MB. Trên iPhone cũ hai lần compile đó đã ăn
   * gần hết 20s, nên mốc này phải rộng hơn — nếu không watchdog báo "timeout" đúng lúc CPU
   * fallback sắp xong, tức là tự tay giết bản sửa của chính mình.
   */
  initTimeoutMs: number;

  /**
   * Chờ GPU delegate bao lâu trước khi chuyển sang CPU.
   *
   * Trên iOS Safari, khởi tạo GPU delegate có thể **treo im lặng** (không resolve, không reject) —
   * xin WebGL context trong lúc `getUserMedia` đang giữ camera là một trong những đường dễ treo
   * nhất. `try/catch` chỉ bắt được promise bị reject, nên không có mốc này thì máy đứng ở
   * "đang tải" cho tới hết `initTimeoutMs` mà KHÔNG BAO GIỜ thử CPU (§31, DU #1).
   */
  gpuInitTimeoutMs: number;

  /**
   * Provider đã sẵn sàng nhưng không có bất kỳ hoạt động detect nào trong khoảng này → coi như
   * vòng lặp đã đứng.
   *
   * "Hoạt động" tính cả frame không thấy người: chỉ cần `detect()` trả về (dù null) là vòng lặp
   * còn sống. Đứng hẳn nghĩa là <video> không giao frame hoặc rAF bị treo.
   */
  stallTimeoutMs: number;

  /**
   * Ngưỡng nghi ngờ chọn sai placement (quyết định #21), tính bằng shoulderWidth / earDistance:
   * chính diện thì vai rộng hơn đầu nhiều, còn profile view thì hai vai chồng nhau nên tỉ lệ tụt.
   *
   * CHƯA ĐO THẬT — giá trị hiện tại là ước lượng, phải chốt lại ở Bước 7 bằng
   * docs/device-test-matrix.md trước khi bật banner gợi ý chuyển mode.
   */
  placementMismatch: {
    /** Dưới mức này ở mode FRONTAL → nghi ngờ người dùng đang đặt máy nghiêng. */
    frontalMinShoulderEarRatio: number;
    /** Trên mức này ở mode SIDE → nghi ngờ người dùng đang đặt máy chính diện. */
    sideMaxShoulderEarRatio: number;
    /** Phải nghi ngờ liên tục bao lâu mới hiện banner (tránh nhấp nháy). */
    sustainedMs: number;
  };
}

export const defaultVisionConfig: VisionConfig = {
  targetInferenceFps: 12,
  minInferenceFps: 6,
  uiUpdateFps: 4,
  degradeAboveP95Ms: 60,
  perfSampleWindow: 60,
  initTimeoutMs: 35_000,
  gpuInitTimeoutMs: 8_000,
  stallTimeoutMs: 8_000,
  placementMismatch: {
    frontalMinShoulderEarRatio: 1.6,
    sideMaxShoulderEarRatio: 2.4,
    sustainedMs: 3000,
  },
};
